// frontend/modules/document-type/components/FileTypeSelector.tsx
import React, { useState } from 'react';
import { Select, Tag, Button, Space, Divider, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { COMMON_FILE_TYPES, FILE_TYPE_CATEGORIES } from '../types/document-type.types';

const { Text } = Typography;
const { Option, OptGroup } = Select;

interface FileTypeSelectorProps {
    value?: string[];
    onChange?: (value: string[]) => void;
    placeholder?: string;
    disabled?: boolean;
}

export const FileTypeSelector: React.FC<FileTypeSelectorProps> = ({
    value = [],
    onChange,
    placeholder = "Select allowed file types",
    disabled = false
}) => {
    const [customType, setCustomType] = useState('');

    const handleChange = (selectedValues: string[]) => {
        onChange?.(selectedValues);
    };

    const handleAddCustomType = () => {
        if (customType && !value.includes(customType.toLowerCase())) {
            const newValue = [...value, customType.toLowerCase()];
            onChange?.(newValue);
            setCustomType('');
        }
    };

    const handleRemoveType = (typeToRemove: string) => {
        const newValue = value.filter(type => type !== typeToRemove);
        onChange?.(newValue);
    };

    const renderSelectedTypes = () => {
        if (value.length === 0) return null;

        return (
            <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Selected file types:</Text>
                <div style={{ marginTop: 4 }}>
                    {value.map(type => (
                        <Tag
                            key={type}
                            closable={!disabled}
                            onClose={() => handleRemoveType(type)}
                            style={{ marginBottom: 4 }}
                        >
                            .{type}
                        </Tag>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div>
            <Select
                mode="multiple"
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                disabled={disabled}
                style={{ width: '100%' }}
                dropdownRender={(menu) => (
                    <>
                        {menu}
                        <Divider style={{ margin: '8px 0' }} />
                        <Space style={{ padding: '0 8px 4px' }}>
                            <input
                                placeholder="Custom file type"
                                value={customType}
                                onChange={(e) => setCustomType(e.target.value.toLowerCase())}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddCustomType();
                                    }
                                }}
                                style={{
                                    border: '1px solid #d9d9d9',
                                    borderRadius: 4,
                                    padding: '4px 8px',
                                    outline: 'none'
                                }}
                            />
                            <Button
                                type="text"
                                icon={<PlusOutlined />}
                                onClick={handleAddCustomType}
                                disabled={!customType || value.includes(customType.toLowerCase())}
                            >
                                Add
                            </Button>
                        </Space>
                    </>
                )}
            >
                {Object.entries(FILE_TYPE_CATEGORIES).map(([category, types]) => (
                    <OptGroup key={category} label={category}>
                        {types.map(type => (
                            <Option key={type} value={type}>
                                .{type}
                            </Option>
                        ))}
                    </OptGroup>
                ))}
                
                <OptGroup label="Other Common Types">
                    {COMMON_FILE_TYPES
                        .filter(type => !Object.values(FILE_TYPE_CATEGORIES).flat().includes(type))
                        .map(type => (
                            <Option key={type} value={type}>
                                .{type}
                            </Option>
                        ))}
                </OptGroup>
            </Select>
            
            {renderSelectedTypes()}
        </div>
    );
};
